import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { TiWarningOutline } from 'react-icons/ti';

import { BaIconImport } from 'components/ui/icons';
import Button from 'components/ui/button';
import Scraper from 'components/contacts/scraper';
import SelectFormField from 'components/form/select';
import CheckboxFormField from 'components/form/checkbox';

import { CloudspongeProvider } from 'hooks/cloudsponge';

import './styles.scss';

const CardCreateContacts = ({
  contacts,
  disabledContacts,
  selectedContacts,
  disabledMessage,
  onChange,
  onImportSuccess,
  marketingId,
  contactType
}) => {
  const [addressBookSort, setAddressBookSort] = useState('lastName');
  const [changedContacts, setChangedContacts] = useState([]);
  const checkedChangedContacts = changedContacts.filter(c => c.checked) || [];

  const allContactsSelected = contacts?.every(c => selectedContacts.find(v => v.contactId === c.contactId)) || (checkedChangedContacts.length === contacts.length - disabledContacts.length);

  let sortFunction = (a, b) => (a.firstName || '').localeCompare((b.firstName || ''));
  if (addressBookSort === 'lastName') {
    sortFunction = (a, b) => (a.lastName || '').localeCompare((b.lastName || ''));
  }

  const onToggleChange = (event, contact) => {
    const checked = event.target.checked;
    const others = changedContacts.filter(c => c.contactId !== contact.contactId);
    setChangedContacts([...others, { ...contact, checked }]);
  }

  const onToggleAll = (event) => {
    const checked = event.target.checked;
    const changableContacts = contacts.filter(c => !disabledContacts.some(d => d.contactId === c.contactId));
    setChangedContacts(changableContacts.map(c => ({ ...c, checked })));
  }

  const onAddressBookSortChange = (name, e) => {
    const newSort = e.props.value || 'lastName';
    if (newSort !== addressBookSort) {
      setAddressBookSort(newSort);
    }
  }

  const isChecked = (c) => {
    const changed = changedContacts?.find(ch => ch.contactId === c.contactId);
    if (changed) {
      return changed.checked;
    }
    if (selectedContacts) {
      return selectedContacts.findIndex(sc => sc.contactId === c.contactId) >= 0;
    }
    return false;
  }

  const onSave = () => {
    onChange(changedContacts);
  }

  let buttonTitle = 'SAVE CHANGES';
  if (checkedChangedContacts.length === changedContacts.length) {
    buttonTitle = `ADD ${checkedChangedContacts.length} ${contactType}${checkedChangedContacts.length > 1 ? 'S' : ''}`
  }

  return (
    <div className="card-create-contacts-container">
      <div className="card-create-contacts-menu">
        <CloudspongeProvider>
          <Scraper
            title={<span className="scraper-button-title"><span>Import Contacts</span><BaIconImport/></span>}
            onSuccess={onImportSuccess}
            trackView={false}
            marketingId={marketingId}
            skipBirthdayRequests
          />
        </CloudspongeProvider>
        <div className="contacts-sort-select">
          <SelectFormField
            id="sort"
            name="sort"
            value={addressBookSort || 'lastName'}
            options={[
              { value: 'firstName', label: 'SORT: First Name' },
              { value: 'lastName', label: 'SORT: Last Name' }
            ]}
            required
            noBorder
            onChange={onAddressBookSortChange}
          />
        </div>
      </div>
      <div className="card-create-contacts-list">
        {contacts?.length > 0 &&
          <>
            <div className="contact-item">
              <div className="contact-item-checkbox">
                <CheckboxFormField
                  id="user-contacts-all"
                  name="user-contact-all"
                  checked={!!allContactsSelected}
                  label={(
                    <span className="contact-item-checkbox-value">
                      <span className="contact-name select-all">{allContactsSelected ? 'DESELECT ALL' : 'SELECT ALL'}</span>
                    </span>
                  )}
                  onChange={onToggleAll}
                />
              </div>
            </div>
            {contacts.sort(sortFunction).map(c => (
              <div className="contact-item" key={`contact-item-${c.contactId}`}>
                <div className="contact-item-checkbox">
                  <CheckboxFormField
                    id={`user-contact-${c.contactId}`}
                    name={`user-contact-${c.contactId}`}
                    checked={isChecked(c)}
                    label={(
                      <div className="contact-item-checkbox-value">
                        <span className="contact-name">{c.firstName} {c.lastName}</span>
                        <span className="contact-email">
                          {c.email}
                          {c.bounceDttm ? <span className="no-print" style={{ color: '#FA2630' }}><TiWarningOutline style={{ verticalAlign: 'middle', fontSize: 14, margin: '-2px 5px 0' }} />Bounced</span> : null}
                        </span>
                        {disabledContacts?.findIndex(dc => dc.contactId === c.contactId) >= 0 && disabledMessage}
                      </div>
                    )}
                    disabled={disabledContacts?.findIndex(dc => dc.contactId === c.contactId) >= 0}
                    onChange={(event) => onToggleChange(event, c)}
                  />
                </div>
              </div>
            ))}
          </>
        }
      </div>
      {changedContacts.length > 0 &&
        <div className="submit-button">
          <Button onClick={onSave}>
            {buttonTitle}
          </Button>
        </div>
      }
    </div>
  );
}

CardCreateContacts.propTypes = {
  contacts: PropTypes.array,
  disabledContacts: PropTypes.array,
  selectedContacts: PropTypes.array,
  disabledMessage: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onImportSuccess: PropTypes.func.isRequired,
  marketingId: PropTypes.number,
  contactType: PropTypes.string.isRequired
};

CardCreateContacts.defaultProps = {
  disabledContacts: [],
  disabledMessage: undefined,
  marketingId: null
};

export default CardCreateContacts;